import { FC, useEffect, useState } from "react";
import { useGlobalMessageActions } from "../../../entities/globalMessage";
import { useMyActions } from "../../../entities/my";
import { IStaffShiftPattern, shiftService } from "../../../entities/shift";
import { AuthError } from "../../../shared/lib/error/AuthError";
import { MyError } from "../../../shared/lib/error/MyError";
import { MyButton, MyButtonSecondary } from "../../../shared/ui/button";
import { MyInput } from "../../../shared/ui/input";
import { Modal } from "../../../shared/ui/modal";
import {
    getDefaultStaffShiftPatternForm,
    getStaffShiftPatternForm,
    IStaffShiftPatternForm,
    validateStaffShiftPatternForm,
} from "../lib/staffShiftPatternForm";
import classes from './staffShiftPatternEdit.module.scss'

interface IProps {
    open: boolean;
    setOpen: (open: boolean) => void;
    userId: string;
    pattern?: IStaffShiftPattern;
    onSuccess: (pattern: IStaffShiftPattern) => void;
}

export const StaffShiftPatternEdit: FC<IProps> = ({open, setOpen, userId, pattern, onSuccess}) => {

    const {setIsAuth} = useMyActions()
    const {setGlobalMessage} = useGlobalMessageActions()

    const [form, setForm] = useState<IStaffShiftPatternForm>(getDefaultStaffShiftPatternForm)
    const [error, setError] = useState<string>('')
    const [isSaving, setIsSaving] = useState<boolean>(false)

    useEffect(() => {
        if(!open) return

        setForm(pattern ? getStaffShiftPatternForm(pattern) : getDefaultStaffShiftPatternForm())
        setError('')
    }, [open, pattern])

    const onClose = (value: boolean) => {
        if(!value) setError('')
        setOpen(value)
    }

    const setFormValue = (key: keyof IStaffShiftPatternForm) => (value: string) => {
        setForm(current => ({...current, [key]: value}))
        setError('')
    }

    const savePattern = async () => {
        const result = validateStaffShiftPatternForm(form)

        if(!result.data){
            setError(result.error ?? 'Проверьте заполненные поля')
            return
        }

        try{
            setIsSaving(true)
            const response = await shiftService.updateStaffShiftPattern({userId, ...result.data})

            setGlobalMessage({message: 'График смен сохранён', type: 'ok'})
            onClose(false)
            onSuccess(response.pattern)
        }
        catch(e){
            console.log(e)

            if(e instanceof AuthError){
                setIsAuth(false)
                setGlobalMessage({message: e.message, type: 'error'})
            }
            else if(e instanceof MyError){
                setGlobalMessage({message: e.message, type: 'error'})
            }
            else{
                setGlobalMessage({message: 'Ошибка при сохранении графика смен', type: 'error'})
            }
        }
        finally{
            setIsSaving(false)
        }
    }

    return (
        <Modal open={open} setOpen={onClose}>
            <section className={classes.modal}>
                <section className={classes.header}>
                    <section>
                        <h3>{pattern ? 'Редактировать график смен' : 'Настроить график смен'}</h3>
                        <p>Изменения применятся к будущим сменам по периоду действия графика. Время смен указывается по МСК.</p>
                    </section>
                    <button type="button" className={classes.closeButton} onClick={() => onClose(false)}>×</button>
                </section>

                <section className={classes.form}>
                    <MyInput type="date" title="Опорная дата" value={form.anchorDate} setValue={setFormValue('anchorDate')} />
                    <MyInput type="number" title="Рабочих дней" min={1} max={31} value={form.workDays} setValue={setFormValue('workDays')} />
                    <MyInput type="number" title="Выходных дней" min={1} max={31} value={form.restDays} setValue={setFormValue('restDays')} />
                    <MyInput title="Начало смены" placeholder="08:00" value={form.startTime} setValue={setFormValue('startTime')} />
                    <MyInput title="Окончание смены" placeholder="22:00" value={form.endTime} setValue={setFormValue('endTime')} />
                    <MyInput type="date" title="Действует с" value={form.effectiveFrom} setValue={setFormValue('effectiveFrom')} />
                    <MyInput type="date" title="Действует до" value={form.effectiveTo} setValue={setFormValue('effectiveTo')} />
                </section>

                {error && <section className={classes.error}>*{error}</section>}

                <section className={classes.actions}>
                    <section className={classes.cancelButton}>
                        <MyButtonSecondary onClick={() => onClose(false)}>Отмена</MyButtonSecondary>
                    </section>
                    <MyButton onClick={savePattern} isLoading={isSaving}>Сохранить</MyButton>
                </section>
            </section>
        </Modal>
    )
}
